import React, { useState, useEffect, useCallback } from 'react'
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Spinner,
  Button,
  DateRangePicker,
  Tabs,
  Tab
} from '@nextui-org/react'
import { CalendarDate, today, getLocalTimeZone, parseDate } from '@internationalized/date'
import { useSearchParams } from 'react-router-dom'
import { Modal, ModalBody } from '~/components/modals/CurieModal'
import { SuperAdminConversationDisplay } from '~/components/super-admin/SuperAdminConversationDisplay'
import { SuperAdminTaskDisplay } from '~/components/super-admin/SuperAdminTaskDisplay'
import {
  getUserActivityLeaderboard,
  getUserTasksByEmail,
  LeaderboardUser,
  UserActivityLeaderboardResponse,
  UserTaskByEmail,
  UserConversationByEmail
} from '~/services/superAdminService'

// Medal colors for top 3
const getMedalColor = (rank: number): string => {
  switch (rank) {
    case 1:
      return 'bg-yellow-400 text-yellow-900' // Gold
    case 2:
      return 'bg-gray-300 text-gray-700' // Silver
    case 3:
      return 'bg-amber-600 text-amber-100' // Bronze
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

const formatNumber = (num: number): string => {
  return num.toLocaleString()
}

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Status badge colors
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-700' },
  ERRORED: { bg: 'bg-red-100', text: 'text-red-700' },
  RUNNING: { bg: 'bg-blue-100', text: 'text-blue-700' },
  SUBMITTED: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  ARCHIVED: { bg: 'bg-gray-100', text: 'text-gray-600' },
  CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-500' }
}

interface DateRange {
  start: CalendarDate
  end: CalendarDate
}

// User Details Modal Component
const UserDetailsModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  user: LeaderboardUser | null
}> = ({ isOpen, onClose, user }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [tasks, setTasks] = useState<UserTaskByEmail[]>([])
  const [conversations, setConversations] = useState<UserConversationByEmail[]>([])
  const [activeTab, setActiveTab] = useState<'conversations' | 'tasks'>('conversations')

  // Conversation messages modal state
  const [isMessagesModalOpen, setIsMessagesModalOpen] = useState(false)
  const [selectedConversation, setSelectedConversation] = useState<UserConversationByEmail | null>(
    null
  )

  // Task details modal state
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null)
  const [selectedWorkspaceUuid, setSelectedWorkspaceUuid] = useState<string | undefined>(undefined)

  // Reset all state when parent modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsLoading(false)
      setTasks([])
      setConversations([])
      setActiveTab('conversations')
      setIsMessagesModalOpen(false)
      setSelectedConversation(null)
      setIsTaskModalOpen(false)
      setSelectedTaskId(null)
      setSelectedWorkspaceUuid(undefined)
    }
  }, [isOpen])

  // Handle parent close - if child modal is open, close child first
  const handleParentClose = () => {
    if (isTaskModalOpen) {
      handleCloseTaskModal()
    } else if (isMessagesModalOpen) {
      handleCloseMessagesModal()
    } else {
      onClose()
    }
  }

  const handleOpenConversation = (conv: UserConversationByEmail) => {
    setSelectedConversation(conv)
    setIsMessagesModalOpen(true)
  }

  const handleCloseMessagesModal = () => {
    setIsMessagesModalOpen(false)
    setSelectedConversation(null)
  }

  const handleOpenTask = (task: UserTaskByEmail) => {
    // Find the workspace UUID from conversations list
    const taskConv = conversations.find(c => c.id === task.conversation_id)
    setSelectedTaskId(task.task_id)
    setSelectedWorkspaceUuid(taskConv?.workspace_uuid || undefined)
    setIsTaskModalOpen(true)
  }

  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false)
    setSelectedTaskId(null)
    setSelectedWorkspaceUuid(undefined)
  }

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return
      setIsLoading(true)
      try {
        const data = await getUserTasksByEmail(user.email)
        setTasks(data.tasks)
        setConversations(data.conversations)
      } catch (error) {
        console.error('Failed to fetch user data:', error)
        setTasks([])
        setConversations([])
      } finally {
        setIsLoading(false)
      }
    }

    if (isOpen && user) {
      fetchUserData()
    }
  }, [isOpen, user])

  if (!user) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleParentClose}
      className='h-[80vh] w-[70vw]'
      hideCloseButton={false}
    >
      <ModalBody className='h-full overflow-hidden flex flex-col'>
        {/* Header */}
        <div className='border-b border-gray-200 pb-4 mb-4'>
          <h2 className='text-xl font-bold'>{user.name}</h2>
          <p className='text-sm text-gray-500'>{user.email}</p>
          <div className='flex gap-4 mt-2 text-sm'>
            <span>
              Credits: <strong>{formatNumber(user.credits_used)}</strong>
            </span>
            <span>
              Tasks: <strong>{formatNumber(user.task_count)}</strong>
            </span>
            <span>
              Messages: <strong>{formatNumber(user.message_count)}</strong>
            </span>
          </div>
        </div>
        {isLoading ? (
          <div className='flex-1 flex items-center justify-center'>
            <Spinner size='lg' />
          </div>
        ) : (
          <div className='flex flex-col h-full'>
            <Tabs
              selectedKey={activeTab}
              onSelectionChange={key => setActiveTab(key as 'conversations' | 'tasks')}
              className='mb-4'
            >
              <Tab key='conversations' title={`Conversations (${conversations.length})`} />
              <Tab key='tasks' title={`Tasks (${tasks.length})`} />
            </Tabs>

            <div className='flex-1 overflow-auto'>
              {activeTab === 'conversations' ? (
                conversations.length === 0 ? (
                  <div className='text-center text-gray-500 py-8'>No conversations found</div>
                ) : (
                  <Table aria-label='User conversations'>
                    <TableHeader>
                      <TableColumn>Title</TableColumn>
                      <TableColumn width={100}>Mode</TableColumn>
                      <TableColumn width={100}>Messages</TableColumn>
                      <TableColumn width={180}>Created</TableColumn>
                      <TableColumn width={80}>Action</TableColumn>
                    </TableHeader>
                    <TableBody>
                      {conversations.map(conv => (
                        <TableRow key={conv.id}>
                          <TableCell>
                            <div className='flex flex-col'>
                              <span className='font-medium text-sm'>{conv.title}</span>
                              <span className='text-xs text-gray-400'>ID: {conv.id}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {conv.mode && (
                              <Chip size='sm' variant='flat'>
                                {conv.mode}
                              </Chip>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className='text-sm text-gray-600'>{conv.message_count}</span>
                          </TableCell>
                          <TableCell className='text-sm text-gray-600'>
                            {formatDate(conv.created_at)}
                          </TableCell>
                          <TableCell>
                            <Button
                              size='sm'
                              variant='flat'
                              color='primary'
                              onClick={() => handleOpenConversation(conv)}
                            >
                              Open
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              ) : tasks.length === 0 ? (
                <div className='text-center text-gray-500 py-8'>No tasks found</div>
              ) : (
                <Table aria-label='User tasks'>
                  <TableHeader>
                    <TableColumn width={100}>Task ID</TableColumn>
                    <TableColumn>Command Type</TableColumn>
                    <TableColumn width={100}>Status</TableColumn>
                    <TableColumn width={180}>Created</TableColumn>
                    <TableColumn width={80}>Action</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {tasks.map(task => {
                      const statusColors = STATUS_COLORS[task.status] || {
                        bg: 'bg-gray-100',
                        text: 'text-gray-600'
                      }
                      return (
                        <TableRow key={task.task_id}>
                          <TableCell>
                            <span className='font-mono text-sm'>#{task.task_id}</span>
                          </TableCell>
                          <TableCell>
                            <Chip size='sm' variant='flat' color='primary'>
                              {task.cmd_type}
                            </Chip>
                          </TableCell>
                          <TableCell>
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors.bg} ${statusColors.text}`}
                            >
                              {task.status}
                            </span>
                          </TableCell>
                          <TableCell className='text-sm text-gray-600'>
                            {formatDate(task.created_at)}
                          </TableCell>
                          <TableCell>
                            <Button
                              size='sm'
                              variant='flat'
                              color='primary'
                              onClick={() => handleOpenTask(task)}
                            >
                              Open
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        )}

        {/* Conversation Messages Modal */}
        <Modal
          isOpen={isMessagesModalOpen}
          onClose={handleCloseMessagesModal}
          className='h-[85vh] w-[80vw]'
          hideCloseButton={false}
        >
          {selectedConversation && (
            <SuperAdminConversationDisplay
              conversationId={selectedConversation.id}
              title={selectedConversation.title}
            />
          )}
        </Modal>

        {/* Task Details Modal */}
        <Modal
          isOpen={isTaskModalOpen}
          onClose={handleCloseTaskModal}
          className='h-[90vh] w-[90vw]'
          hideCloseButton={false}
        >
          <ModalBody className='h-full overflow-hidden p-0'>
            {selectedTaskId && (
              <SuperAdminTaskDisplay
                taskId={selectedTaskId}
                workspaceUuid={selectedWorkspaceUuid}
                onClose={handleCloseTaskModal}
              />
            )}
          </ModalBody>
        </Modal>
      </ModalBody>
    </Modal>
  )
}

const UserActivityLeaderboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState<UserActivityLeaderboardResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [limit] = useState(50)

  // User details modal state
  const [selectedUser, setSelectedUser] = useState<LeaderboardUser | null>(null)
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)

  // Default to last 30 days, or load from URL params
  const defaultEndDate = today(getLocalTimeZone())
  const defaultStartDate = defaultEndDate.subtract({ days: 30 })

  const [dateRange, setDateRange] = useState<DateRange | null>(() => {
    const startParam = searchParams.get('startDate')
    const endParam = searchParams.get('endDate')
    if (startParam && endParam) {
      try {
        return {
          start: parseDate(startParam),
          end: parseDate(endParam)
        }
      } catch {
        // Fall back to default if parsing fails
      }
    }
    return {
      start: defaultStartDate,
      end: defaultEndDate
    }
  })

  // Sync date range to URL
  useEffect(() => {
    if (!dateRange) return
    const params = new URLSearchParams(searchParams)
    const startStr = `${dateRange.start.year}-${String(dateRange.start.month).padStart(2, '0')}-${String(dateRange.start.day).padStart(2, '0')}`
    const endStr = `${dateRange.end.year}-${String(dateRange.end.month).padStart(2, '0')}-${String(dateRange.end.day).padStart(2, '0')}`
    params.set('startDate', startStr)
    params.set('endDate', endStr)
    setSearchParams(params, { replace: true })
  }, [dateRange, setSearchParams, searchParams])

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      let startDateStr: string | undefined
      let endDateStr: string | undefined

      if (dateRange) {
        // Send date strings in YYYY-MM-DD format along with user's timezone
        // The API will convert to UTC based on the timezone
        startDateStr = `${dateRange.start.year}-${String(dateRange.start.month).padStart(2, '0')}-${String(dateRange.start.day).padStart(2, '0')}`
        endDateStr = `${dateRange.end.year}-${String(dateRange.end.month).padStart(2, '0')}-${String(dateRange.end.day).padStart(2, '0')}`
      }

      // Get user's timezone from browser
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

      const response = await getUserActivityLeaderboard({
        startDate: startDateStr,
        endDate: endDateStr,
        timezone: userTimezone,
        limit
      })

      setData(response)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch leaderboard data')
    } finally {
      setIsLoading(false)
    }
  }, [dateRange, limit])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleRefresh = () => {
    fetchData()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDateRangeChange = (value: any) => {
    setDateRange(value as DateRange | null)
  }

  // Quick date range presets
  const setPreset = (days: number) => {
    const end = today(getLocalTimeZone())
    const start = end.subtract({ days })
    setDateRange({ start, end })
  }

  const handleViewUser = (user: LeaderboardUser) => {
    setSelectedUser(user)
    setIsUserModalOpen(true)
  }

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false)
    setSelectedUser(null)
  }

  if (isLoading && !data) {
    return (
      <div className='flex justify-center items-center h-64'>
        <Spinner size='lg' />
      </div>
    )
  }

  if (error) {
    return (
      <Card className='w-full'>
        <CardBody>
          <div className='text-center text-danger'>
            <p>Error loading leaderboard: {error}</p>
            <Button color='primary' className='mt-4' onClick={handleRefresh}>
              Retry
            </Button>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Filters Card */}
      <Card className='w-full'>
        <CardHeader>
          <h3 className='text-lg font-semibold'>User Activity Leaderboard</h3>
        </CardHeader>
        <CardBody>
          <div className='flex gap-4 items-end flex-wrap'>
            <DateRangePicker
              label='Time Period'
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              value={dateRange as any}
              onChange={handleDateRangeChange}
              className='max-w-xs'
              showMonthAndYearPickers
            />

            {/* Quick presets */}
            <div className='flex gap-2'>
              <Button size='sm' variant='flat' onClick={() => setPreset(7)}>
                Last 7 days
              </Button>
              <Button size='sm' variant='flat' onClick={() => setPreset(30)}>
                Last 30 days
              </Button>
              <Button size='sm' variant='flat' onClick={() => setPreset(90)}>
                Last 90 days
              </Button>
            </div>

            <Button color='primary' onClick={handleRefresh} isLoading={isLoading}>
              Apply Filters
            </Button>
          </div>

          {data?.metadata && (
            <div className='mt-4 p-3 bg-gray-50 rounded-lg space-y-1'>
              <p className='text-sm text-gray-600'>
                <strong>Query Period:</strong> {data.metadata.start_date || 'All time'} to{' '}
                {data.metadata.end_date || 'Now'}
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Summary Stats */}
      {data?.summary && (
        <div className='grid grid-cols-4 gap-4'>
          <Card>
            <CardBody className='text-center'>
              <div className='text-3xl font-bold text-primary'>
                {formatNumber(data.summary.total_users)}
              </div>
              <div className='text-sm text-gray-600'>Active Users</div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className='text-center'>
              <div className='text-3xl font-bold text-success'>
                {formatNumber(data.summary.total_credits)}
              </div>
              <div className='text-sm text-gray-600'>Total Credits Used</div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className='text-center'>
              <div className='text-3xl font-bold text-secondary'>
                {formatNumber(data.summary.total_tasks)}
              </div>
              <div className='text-sm text-gray-600'>Total Tasks</div>
            </CardBody>
          </Card>
          <Card>
            <CardBody className='text-center'>
              <div className='text-3xl font-bold text-warning'>
                {formatNumber(data.summary.total_messages)}
              </div>
              <div className='text-sm text-gray-600'>Total Messages</div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Leaderboard Table */}
      <Card className='w-full'>
        <CardHeader>
          <h3 className='text-lg font-semibold'>
            Top Users by Credits Used
            {data?.leaderboard && (
              <span className='text-sm font-normal text-gray-500 ml-2'>
                (Showing {data.leaderboard.length} users)
              </span>
            )}
          </h3>
        </CardHeader>
        <CardBody>
          {!data?.leaderboard || data.leaderboard.length === 0 ? (
            <p className='text-center text-gray-500 py-8'>
              No user activity found for the selected period
            </p>
          ) : (
            <Table aria-label='User activity leaderboard'>
              <TableHeader>
                <TableColumn width={80}>Rank</TableColumn>
                <TableColumn>User</TableColumn>
                <TableColumn width={120}>Credits Used</TableColumn>
                <TableColumn width={100}>Tasks</TableColumn>
                <TableColumn width={100}>Messages</TableColumn>
                <TableColumn width={120}>Conversations</TableColumn>
                <TableColumn width={100}>Actions</TableColumn>
              </TableHeader>
              <TableBody>
                {data.leaderboard.map((user: LeaderboardUser) => (
                  <TableRow key={user.user_id}>
                    <TableCell>
                      <span
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${getMedalColor(user.rank)}`}
                      >
                        {user.rank}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className='flex flex-col'>
                        <span className='font-medium text-sm'>{user.name}</span>
                        <span className='text-xs text-gray-500'>{user.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Chip size='sm' variant='flat' color='success'>
                        {formatNumber(user.credits_used)}
                      </Chip>
                    </TableCell>
                    <TableCell>
                      <Chip size='sm' variant='flat' color='secondary'>
                        {formatNumber(user.task_count)}
                      </Chip>
                    </TableCell>
                    <TableCell>
                      <Chip size='sm' variant='flat' color='warning'>
                        {formatNumber(user.message_count)}
                      </Chip>
                    </TableCell>
                    <TableCell>
                      <span className='text-sm'>{formatNumber(user.conversation_count)}</span>
                    </TableCell>
                    <TableCell>
                      <Button
                        size='sm'
                        color='primary'
                        variant='flat'
                        onClick={() => handleViewUser(user)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* User Details Modal */}
      <UserDetailsModal
        isOpen={isUserModalOpen}
        onClose={handleCloseUserModal}
        user={selectedUser}
      />
    </div>
  )
}

export default UserActivityLeaderboard
